import { BorderProps, ColorProps, Flex } from '@chakra-ui/react';
import { IconName } from '../../medias';
import { Paragraph } from '../../typography';
import { ToastAction } from './components/toast-actions';
import { ToastBody } from './components/toast-body';
import { ToastCloseButton } from './components/toast-close-button';
import { ToastHeader } from './components/toast-header';
import { ToastIcon } from './components/toast-icon';
import { ToastProps, ToastVariant } from './toast.types';

type ToastContent = {
  icon: IconName;
  iconColor: ColorProps['color'];
  backgroundColor: ColorProps['color'];
  actionColor: ColorProps['color'];
  border?: BorderProps;
};

const toastVariantContent: {
  [key in ToastVariant]: ToastContent;
} = {
  success: {
    iconColor: 'icon.status.onSuccessContainer',
    icon: IconName.Success,
    backgroundColor: 'background.status.default.success',
    actionColor: 'text.status.onSuccessDefaultContainer',
    border: {
      border: '1px solid',
      borderColor: 'background.container.success'
    }
  },
  warning: {
    iconColor: 'icon.status.onWarningContainer',
    icon: IconName.Warning,
    backgroundColor: 'background.status.default.warning',
    actionColor: 'text.status.onWarningDefaultContainer',
    border: {
      border: '1px solid',
      borderColor: 'background.container.warning'
    }
  },
  error: {
    iconColor: 'icon.status.onAlertContainer',
    icon: IconName.Info,
    backgroundColor: 'background.status.default.alert',
    actionColor: 'icon.status.onAlertContainer',
    border: {
      border: '1px solid',
      borderColor: 'background.container.alert'
    }
  },
  default: {
    iconColor: 'icon.status.success',
    icon: IconName.Info,
    backgroundColor: 'background.surface',
    actionColor: 'text.link'
  }
};

export function Toast(props: ToastProps): JSX.Element | null {
  const { type } = props;

  const toastContent = toastVariantContent[type || 'default'];
  return (
    <Flex
      borderRadius="radii.x1"
      margin={'space_unit.x2'}
      py="space_unit.x2"
      px="space_unit.x2"
      w={'360px'}
      backgroundColor={toastContent.backgroundColor}
      border={toastContent.border?.border}
      borderColor={toastContent.border?.borderColor}
    >
      <Flex w="100%" flexDir="column">
        <ToastMain {...props} />
      </Flex>
    </Flex>
  );
}

function ToastMain({
  header,
  body,
  icon,
  action,
  type,
  withIcon,
  onClose,
  closable
}: ToastProps): JSX.Element {
  const toastContent = toastVariantContent[type || 'success'];

  if (header) {
    return (
      <Flex p="space_unit.x1" gap="10px" flexDir="column">
        <Flex w="full" justifyContent="space-between">
          <Flex>
            <ToastIcon
              icon={icon || toastContent.icon}
              color={toastContent.iconColor}
              withIcon={withIcon}
            />
            <ToastHeader header={header} />
          </Flex>
          <ToastCloseButton closable={closable} onClose={onClose} />
        </Flex>

        {body && (
          <Paragraph size="sm" color="palette_gray.80">
            {body}
          </Paragraph>
        )}
        <ToastAction action={action} color={toastContent.actionColor} />
      </Flex>
    );
  }
  return (
    <Flex
      p="space_unit.x1"
      alignItems="center"
      justifyContent="space-between"
      gap="space_unit.x1"
    >
      {!action && (
        <ToastIcon
          icon={icon || toastContent.icon}
          color={toastContent.iconColor}
          withIcon={withIcon}
        />
      )}
      <ToastBody body={body} />
      <ToastAction action={action} color={toastContent.actionColor} />
      <ToastCloseButton closable={closable} onClose={onClose} />
    </Flex>
  );
}
